<script lang="ts" setup>
import StorageService from '../../../runtime/services/StorageService';

const { $log } = useLogging();
const { $alert } = useAlerts();
const { $events } = useEvents();
const { $t } = useLocalization();

defineOptions({
    name: 'FilemanagerResizeModal'
});

const emit = defineEmits<{
    (e: 'reload'): void;
}>();

const resource = ref<FileResource>(null);
const modalRef = ref<any>(null);

const width = ref(0);
const height = ref(0);

const form = Form.create({
    width: 0,
    height: 0
});
const resizeImage = async (): Promise<void> =>
{
    try
    {
        form.wait();

        await StorageService.resizeImage(resource.value.id, form.data());

        $alert.success($t('[[[Rozmiar zdjęcia został zmieniony.]]]'));
        modalRef.value.hide();
        emit('reload');
        $events.$emit('filemanager::buttons::cropper', false);
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message),
            422: (ex: any) => form.$errors.record(ex.data.errors)
        });
    }
    finally
    {
        form.continue();
    }
};

const cropChanged = (params: any): void =>
{
    form.width = params.width;
    form.height = params.height;
    form.x = params.x;
    form.y = params.y;
};

onMounted((): void =>
{
    $events.$on('filemanager::cropper::changed', cropChanged);
});

onUnmounted((): void =>
{
    $events.$off('filemanager::cropper::changed', cropChanged);
});

const showModal = (item: FileResource): void =>
{
    width.value = parseInt(item.meta?.width) ?? 0;
    height.value = parseInt(item.meta?.height) ?? 0;

    resource.value = item;
    modalRef.value.show();
};

const handleInput = (type: 'width' | 'height'): void =>
{
    const { width: baseWidth, height: baseHeight } = resource.value?.meta ?? {};

    if (type === 'width')
        height.value = Math.round((width.value * parseInt(baseHeight)) / parseInt(baseWidth));
    else if (type === 'height')
        width.value = Math.round((height.value * parseInt(baseWidth)) / parseInt(baseHeight));

    form.width = width.value;
    form.height = height.value;
};

defineExpose({
    showModal
});
</script>

<template>
    <IdeoModal
        ref="modalRef"
        :title="$t('[[[Zmiana rozmiaru obrazu]]]')"
        hide-footer
        modal-class="filemanager-modal"
        backdrop-class="filemanager-backdrop"
    >
        <form @submit.prevent="resizeImage" @keydown="form.$errors.clear($event.target.name)">
            <IdeoFormGroup
                :label="$t('[[[Szerokość]]]')"
                :invalid-feedback="form.$errors.first('width')"
                :state="form.$errors.state('width')"
                label-cols="3"
                label-for="dimension-x"
            >
                <IdeoInputGroup append="px">
                    <IdeoFormInput id="dimension-x" v-model="width" type="number" name="width" @input="handleInput('width')" />
                </IdeoInputGroup>
            </IdeoFormGroup>

            <IdeoFormGroup
                :label="$t('[[[Wysokość]]]')"
                :invalid-feedback="form.$errors.first('height')"
                :state="form.$errors.state('height')"
                label-cols="3"
                label-for="dimension-y"
            >
                <IdeoInputGroup append="px">
                    <IdeoFormInput id="dimension-y" v-model="height" type="number" name="height" @input="handleInput('height')" />
                </IdeoInputGroup>
            </IdeoFormGroup>

            <div class="text-end">
                <ActionButton
                    variant="light"
                    icon="far fa-ban"
                    :text="$t('[[[Anuluj]]]')"
                    :disabled="!form.active()"
                    @click.prevent="modalRef.hide()"
                />

                <ActionButton
                    type="submit"
                    variant="primary"
                    icon="far fa-check"
                    :disabled="!form.active()"
                    :loading="!form.active()"
                    :text="$t('[[[Wykonaj]]]')"
                />
            </div>
        </form>
    </IdeoModal>
</template>
